import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Icon,
  Select,
  V2Badge,
  V2Button,
  V2Card,
  V2EmptyState,
  V2SearchInput,
  type SakhtarIconName,
  type V2BadgeTone,
} from '@sakhtar/design-system/react';
import {
  DOC_SORT_OPTIONS,
  GENERIC_UNAVAILABLE_COPY,
  UNAVAILABLE_COPY,
  matchesQuery,
  sortDocs,
  sumRial,
  type Customer360Tab,
  type DocSort,
  type Tone,
} from './customer360Model.js';

const fa = (n: number) => n.toLocaleString('fa-IR');

export function toneOf(tone: Tone): V2BadgeTone {
  return tone;
}

/** Copies text; shows «کپی شد» for a moment. Clipboard access may be denied — then it says so. */
export function CopyButton({ text, label }: { readonly text: string; readonly label: string }) {
  const [state, setState] = useState<'idle' | 'done' | 'failed'>('idle');
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setState('done');
    } catch {
      setState('failed');
    }
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setState('idle'), 1800);
  }

  return (
    <button
      type="button"
      className="c360-copy"
      aria-label={`کپی ${label}`}
      onClick={() => void copy()}
    >
      <span aria-live="polite">
        {state === 'done' ? 'کپی شد' : state === 'failed' ? 'کپی نشد' : 'کپی'}
      </span>
    </button>
  );
}

/** A labelled value. `undefined` means hidden by permission (not rendered), `null`/'' means not recorded. */
export function Field({
  label,
  value,
  ltr = false,
  copy = false,
  wide = false,
  children,
}: {
  readonly label: string;
  readonly value?: string | null | undefined;
  readonly ltr?: boolean;
  readonly copy?: boolean;
  readonly wide?: boolean;
  readonly children?: ReactNode;
}) {
  const empty = children === undefined && !value;
  return (
    <div className={`c360-field${wide ? ' c360-field--wide' : ''}`}>
      <dt>{label}</dt>
      <dd className={empty ? 'is-empty' : undefined}>
        {children ??
          (value ? (
            <>
              <span dir={ltr ? 'ltr' : undefined}>{value}</span>
              {copy ? <CopyButton text={value} label={label} /> : null}
            </>
          ) : (
            'ثبت نشده'
          ))}
      </dd>
    </div>
  );
}

export function KpiTile({
  label,
  value,
  sub,
  muted = false,
  active = false,
  tone,
  onClick,
}: {
  readonly label: string;
  readonly value: ReactNode;
  readonly sub?: ReactNode;
  readonly muted?: boolean;
  readonly active?: boolean;
  /** Accent colour of the tile (semantic: documents blue, orders green, deals violet…). */
  readonly tone?: 'blue' | 'green' | 'violet' | 'amber' | 'teal' | 'pink' | 'slate';
  readonly onClick?: () => void;
}) {
  const body = (
    <>
      <span className="c360-kpi__label">{label}</span>
      <span className={`c360-kpi__value${muted ? ' c360-kpi__value--muted' : ''}`}>{value}</span>
      {sub ? <span className="c360-kpi__sub">{sub}</span> : null}
    </>
  );
  const className = `c360-kpi${active ? ' is-active' : ''}`;
  return onClick ? (
    <button
      type="button"
      className={className}
      data-tone={tone}
      onClick={onClick}
      aria-pressed={active}
    >
      {body}
    </button>
  ) : (
    <div className={className} data-tone={tone}>
      {body}
    </div>
  );
}

export function Skeleton360() {
  return (
    <div className="c360" aria-busy="true" aria-live="polite">
      <div className="c360-head">
        <div className="c360-head__id">
          <span className="sk-skel" style={{ display: 'block', width: 44, height: 44 }} />
          <div className="c360-head__text" style={{ flex: 1 }}>
            <span className="sk-skel" style={{ display: 'block', width: 160, height: 22 }} />
            <span className="sk-skel" style={{ display: 'block', width: 110, height: 12 }} />
          </div>
        </div>
      </div>
      <div className="c360-kpis">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="c360-kpi">
            <span className="sk-skel" style={{ display: 'block', width: '50%', height: 12 }} />
            <span className="sk-skel" style={{ display: 'block', width: '70%', height: 22 }} />
          </div>
        ))}
      </div>
      <V2Card>
        <div className="c360-skeleton">
          {[0, 1, 2].map((i) => (
            <span key={i} className="sk-skel" style={{ display: 'block', height: 14 }} />
          ))}
        </div>
      </V2Card>
    </div>
  );
}

/** Honest "no data source yet" state: Persian explanation, never the raw backend reason. */
export function UnavailablePanel({
  tab,
  base,
}: {
  readonly tab: Customer360Tab;
  readonly base: string;
}) {
  const copy = UNAVAILABLE_COPY[tab] ?? GENERIC_UNAVAILABLE_COPY;
  return (
    <V2Card>
      <div className="c360-unavailable" role="status">
        <Icon name="info" size="md" />
        <div className="c360-unavailable__title">{copy.title}</div>
        <p className="c360-unavailable__desc">{copy.description}</p>
        {copy.link ? (
          <Link className="sk-btn sk-btn--secondary" to={`${base}/${copy.link.to}`}>
            {copy.link.label}
          </Link>
        ) : null}
      </div>
    </V2Card>
  );
}

export interface DocColumn<Row> {
  readonly key: string;
  readonly header: string;
  readonly render: (row: Row) => ReactNode;
  readonly align?: 'start' | 'end';
  /** Card layout: `title` is the link line, `badge` sits beside it, others become label/value pairs. */
  readonly cardRole?: 'title' | 'badge' | 'pair';
}

export interface DocFilter {
  readonly id: string;
  readonly label: string;
}

/**
 * Searchable, filterable, sortable list of customer documents. Renders a table
 * on wide screens and cards under 700px. The footer total is an exact
 * BigInt sum of what is currently shown.
 */
export function DocPanel<Row>({
  rows,
  rowKey,
  columns,
  searchFields,
  searchPlaceholder,
  filterOf,
  amountOf,
  refOf,
  formatAmount,
  totalLabel,
  note,
  capped = false,
  empty,
}: {
  readonly rows: readonly Row[];
  readonly rowKey: (row: Row, index: number) => string;
  readonly columns: readonly DocColumn<Row>[];
  readonly searchFields: (row: Row) => readonly (string | null | undefined)[];
  readonly searchPlaceholder: string;
  readonly filterOf?: (row: Row) => DocFilter;
  /** Rows without an amount (activities, history) omit these three: no amount sort, no total. */
  readonly amountOf?: (row: Row) => string;
  readonly refOf: (row: Row) => string;
  readonly formatAmount?: (digits: string) => string;
  readonly totalLabel?: string;
  readonly note?: ReactNode;
  /** The list may be truncated by the backend: count and sum are then shown as partial, never as totals. */
  readonly capped?: boolean;
  readonly empty: { readonly icon: SakhtarIconName; readonly title: string };
}) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<string | null>(null);
  const [sort, setSort] = useState<DocSort>('default');

  const filters = useMemo(() => {
    if (!filterOf) return [];
    const counts = new Map<string, { label: string; count: number }>();
    for (const row of rows) {
      const f = filterOf(row);
      const entry = counts.get(f.id);
      if (entry) entry.count += 1;
      else counts.set(f.id, { label: f.label, count: 1 });
    }
    return [...counts.entries()].map(([id, v]) => ({ id, label: v.label, count: v.count }));
  }, [rows, filterOf]);

  const visible = useMemo(() => {
    const matched = rows.filter(
      (row) =>
        (filter === null || filterOf?.(row).id === filter) &&
        matchesQuery(searchFields(row), query),
    );
    return sortDocs(matched, sort, amountOf ?? (() => '0'), refOf);
  }, [rows, filter, query, sort, filterOf, searchFields, amountOf, refOf]);

  if (rows.length === 0) {
    return <V2EmptyState icon={empty.icon} title={empty.title} compact />;
  }

  const hasFilters = query.trim() !== '' || filter !== null;
  const titleCol = columns.find((c) => c.cardRole === 'title');
  const badgeCols = columns.filter((c) => c.cardRole === 'badge');
  const pairCols = columns.filter((c) => (c.cardRole ?? 'pair') === 'pair');

  return (
    <V2Card clipped>
      <div className="c360-toolbar">
        <V2SearchInput
          placeholder={searchPlaceholder}
          value={query}
          onChange={setQuery}
          width={240}
        />
        {filters.length > 1
          ? filters.map((f) => (
              <button
                key={f.id}
                type="button"
                className="c360-chip"
                aria-pressed={filter === f.id}
                onClick={() => setFilter((cur) => (cur === f.id ? null : f.id))}
              >
                {f.label} ({fa(f.count)})
              </button>
            ))
          : null}
        <div className="c360-toolbar__end">
          <Select
            aria-label="مرتب‌سازی"
            value={sort}
            onChange={(e) => setSort(e.target.value as DocSort)}
          >
            {DOC_SORT_OPTIONS.filter(
              (o) => amountOf !== undefined || !o.value.startsWith('amount'),
            ).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {note ? <p className="c360-note">{note}</p> : null}

      {visible.length === 0 ? (
        <V2EmptyState
          icon="search"
          title="موردی با این فیلترها پیدا نشد."
          compact
          action={
            hasFilters ? (
              <V2Button
                onClick={() => {
                  setQuery('');
                  setFilter(null);
                }}
              >
                پاک کردن فیلترها
              </V2Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <div className="c360-table sk-table-scroll">
            <table className="sk-table">
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c.key} style={c.align === 'end' ? { textAlign: 'end' } : undefined}>
                      {c.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((row, index) => (
                  <tr key={rowKey(row, index)}>
                    {columns.map((c) => (
                      <td
                        key={c.key}
                        className={c.align === 'end' ? 'sk-num' : undefined}
                        style={c.align === 'end' ? { textAlign: 'end' } : undefined}
                      >
                        {c.render(row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="c360-cards">
            {visible.map((row, index) => (
              <article key={rowKey(row, index)} className="c360-card">
                <div className="c360-card__head">
                  {titleCol ? titleCol.render(row) : null}
                  <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
                    {badgeCols.map((c) => (
                      <span key={c.key}>{c.render(row)}</span>
                    ))}
                  </span>
                </div>
                <dl className="c360-card__pairs">
                  {pairCols.map((c) => (
                    <div key={c.key}>
                      <dt>{c.header}</dt>
                      <dd>{c.render(row)}</dd>
                    </div>
                  ))}
                </dl>
              </article>
            ))}
          </div>
        </>
      )}

      <div className="c360-foot">
        <span>
          {fa(visible.length)}
          {hasFilters ? ` از ${fa(rows.length)}` : ''}
          {capped && !hasFilters ? '+' : ''} مورد
        </span>
        {amountOf && formatAmount && totalLabel ? (
          <strong title={totalLabel}>
            {capped ? `${totalLabel} (فقط ${fa(rows.length)} مورد اول، نه جمع کل)` : totalLabel}:{' '}
            {formatAmount(sumRial(visible.map(amountOf)))}
          </strong>
        ) : null}
      </div>
    </V2Card>
  );
}

export function StatusBadge({ label, tone }: { readonly label: string; readonly tone: Tone }) {
  return <V2Badge tone={toneOf(tone)}>{label}</V2Badge>;
}
